import { Box, Button, Grid, IconButton, Stack, Typography } from "@mui/material";
import { FormProvider, RHFDateField, RHFRadioGroup, RHFSearchableSelectField, RHFTextField } from "../../common/hook-form";
import _ from "lodash";
import React, { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { AddCircleTwoTone, RemoveCircleTwoTone } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useGetMerchantQuery } from "../../redux/commonRTK";
import { getMerchant } from "../../helpers/ReduxHelpers";
import { addressString, filterByProp, formatDate } from "../../helpers/UtilityHelper";
import { FormSkeleton } from "../../common/Skeletons";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup"
import PageTitle from "../../common/PageTitle";
import { apiConstants, postGeneric } from "../../common/constants";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { primaryBtnSx, secondaryBtnSx, pageContainerSx } from '../../styles/patterns';
import { WHITE, BORDER, PRIMARY_BLUE } from '../../styles/foundation/colors';
import { SHADOW } from '../../styles/foundation/effects';

const emptyItemObject = {
    units: 0,
    additional: {
        weight: 0
    }
};

function CreatePickupRequest2({ setActiveStep, creatingOrder }) {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);

    const validationSchema = Yup.object().shape({
        warehouseName: Yup.string().required('Warehouse name is required'),
        // pickup_date: Yup.date()
        //     .required('Pickup date is required')
        //     .nullable()
        //     .min(new Date(), 'Pickup date cannot be in the past'),
        pickup_date: Yup.date()
        .required('Pickup date is required')
        .nullable()
        .test('pickup-date-validation', 'Pickup date cannot be in the past. After 1 PM IST, only future dates are allowed.', function(value) {
            if (!value) return true; // Let required validation handle empty values

            // Get current time in IST
            const now = new Date();
            const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
            const istTime = new Date(now.getTime() + istOffset);
            const currentHour = istTime.getUTCHours();

            // Normalize the selected date to start of day
            const selectedDate = new Date(value);
            selectedDate.setHours(0, 0, 0, 0);

            let minAllowedDate;
            // If current IST time is before 1 PM (13:00), allow today
            if (currentHour < 13) {
                const today = new Date();
                today.setHours(0, 0, 0, 0); // Set to start of day
                minAllowedDate = today;
            } else {
                // If after 1 PM IST, only allow tomorrow onwards
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(0, 0, 0, 0); // Set to start of day
                minAllowedDate = tomorrow;
            }

            return selectedDate >= minAllowedDate;
        }),
        payment_mode: Yup.string()
            .required('Payment mode is required')
            .oneOf(['PREPAID', 'COD'], 'Invalid payment mode'),
        zip: Yup.string()
            .required('Delivery Zip Code is required')
            .matches(/^\d{6}$/, 'Zip code must be exactly 6 digits'),
        item_list: Yup.array().of(
            Yup.object().shape({
                units: Yup.number()
                    .required('Number of units is required')
                    .positive('Must be a positive number')
                    .integer('Must be a whole number'),
                additional: Yup.object().shape({
                    weight: Yup.number()
                        .required('Weight is required')
                        .positive('Must be a positive number')
                })
            })
        ).required('At least one item is required')
    });

    const methods = useForm({
        defaultValues: {
            payment_mode: 'PREPAID',
            appointment_delivery: false,
            warehouseName: '',
            zip: '',
            pickup_date: null,
            item_list: [
                {
                    units: '',
                    additional: {
                        weight: '',
                        height: 10,
                        breadth: 10,
                        length: 10
                    }
                }
            ]
        },
        resolver: yupResolver(validationSchema)
    });

    const { handleSubmit, control, watch, setValue, reset } = methods;

    const invoiceValue = watch('invoice_value')
    const { append, remove, fields } = useFieldArray({
        name: 'item_list',
        control: control,
    })

    React.useEffect(() => {
        setValue('selling_price', invoiceValue)

        // eslint-disable-next-line
    }, [invoiceValue])


    React.useEffect(() => {
        if (fields.length === 0) {
            append(emptyItemObject)
        }

        // eslint-disable-next-line
    }, [fields])

    const merchant = getMerchant();
    const { data, isLoading, isFetching } = useGetMerchantQuery({ id: merchant.id })

    // const pickupLocations = data?.pickup_details || [];
    const pickupLocations = filterByProp(data?.pickup_details, "is_deleted") || [];

    const pickupLocationOptions = _(pickupLocations)
        .filter({ status: 'ACTIVE', address_type: 'PICKUP',shipmentType: "B2B" })
        .map((loc) => ({
            value: loc.name,
            label: addressString(loc)
        })).value()

    if (isLoading || isFetching) {
        return <FormSkeleton />
    }

    const onSubmit = (values) => {
        setIsCreatingOrder(true)
        const formData = JSON.parse(JSON.stringify(values));
        // Transform the data into the new format
        const request = {
            warehouseName: formData.warehouseName,
            merchantOid: merchant.id,
            dropoffLocation: {
                zip: formData.zip
            },
            dimensions: formData.item_list.map(item => ({
                count: item.units,
                weight: item.additional.weight * 1000, // Converting to grams
                height: 10,
                width: 10,
                length: 10

            })),
            orderReadyDate: formatDate(formData.pickup_date, "yyyy-MM-dd"),
            orderType: "FORWARD",
            paymentType: formData.payment_mode === 'PREPAID' ? 'PREPAID' : 'COD',
            appointmentDelivery: formData.appointment_delivery
        };

        postGeneric(apiConstants.PICKUP_B2B_CREATE_ORDER, request)
            .then((response) => {
                enqueueSnackbar("Successfully Created!", { variant: 'success' });
                navigate("/ptl-pickup-requests");
            }).catch((err) => {
                const message = err?.response?.data?.message || err.message;
                enqueueSnackbar(message || "Error creating order", { variant: "error" });
            }).finally(() => {
                setIsCreatingOrder(false)
            })
    }


    return (
        <Box sx={pageContainerSx}>
            <PageTitle subTitle="Schedule a pickup for your PTL shipments">Create Pickup Request</PageTitle>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box sx={{ bgcolor: WHITE, border: '1px solid ' + BORDER, borderRadius: '12px', boxShadow: SHADOW.card, p: { xs: 2.5, md: 3.5 } }}>
                        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sx={{ mb: 3 }}>
                                    <RHFSearchableSelectField
                                        options={pickupLocationOptions}
                                        width="100%"
                                        label="Please Select Pickup Location *"
                                        name="warehouseName"
                                        autoFocus={true}
                                        size="small"
                                    />
                                    <Typography sx={{ fontSize: 12, color: '#637381', mt: 0.5 }}>
                                        Pickup location not listed?{' '}
                                        <a href="/merchant-step2" target="_blank" rel="noopener noreferrer" style={{ color: '#1565c0', textDecoration: 'none', fontWeight: 500 }}>
                                            Create new
                                        </a>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <RHFTextField
                                        name="zip"
                                        label="Delivery Zip Code"
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <RHFDateField
                                        name="pickup_date"
                                        label="Pickup Date"
                                        // minDate={(new Date())}
                                        minDate={(() => {
                                            // Get current time in IST
                                            const now = new Date();
                                            const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
                                            const istTime = new Date(now.getTime() + istOffset);
                                            const currentHour = istTime.getUTCHours();

                                            // If current IST time is before 1 PM (13:00), allow today
                                            if (currentHour < 13) {
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0); // Set to start of day
                                                return today;
                                            } else {
                                                // If after 1 PM IST, only allow tomorrow onwards
                                                const tomorrow = new Date();
                                                tomorrow.setDate(tomorrow.getDate() + 1);
                                                tomorrow.setHours(0, 0, 0, 0); // Set to start of day
                                                return tomorrow;
                                            }
                                        })()}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <RHFRadioGroup
                                        label="Payment Mode"
                                        name="payment_mode"
                                        options={[
                                            { label: 'PREPAID', value: 'PREPAID' },
                                            { label: 'COD', value: 'COD' },
                                        ]}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="caption" fontWeight={500}><strong>Note:</strong> All package Weight must be specified in kgs</Typography>
                                </Grid>
                                {fields.map((field, index) => (
                                    <Grid item xs={12} key={field.id}>
                                        <Stack direction="row" columnGap={1} alignItems="center" sx={{
                                            backgroundColor: PRIMARY_BLUE + '1a',
                                            borderRadius: 1,
                                            p: 2,
                                            border: '1px solid ' + BORDER
                                        }}>
                                            <RHFTextField name={`item_list.${index}.units`} label="No of Boxes" type="number" size="small" />
                                            <RHFTextField name={`item_list.${index}.additional.weight`} label="Weight" type="number" size="small" />
                                            {((index + 1) === fields.length) && (
                                                <IconButton size="small" color="primary" onClick={() => append(emptyItemObject)}>
                                                    <AddCircleTwoTone />
                                                </IconButton>
                                            )}
                                            {(fields.length !== 1) && (
                                                <IconButton size="small" color="error" onClick={() => remove(index)}>
                                                    <RemoveCircleTwoTone />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </Grid>
                                ))}
                                <Grid item xs={12}>
                                    <Stack direction="row" columnGap={1} sx={{ mt: 3 }}>
                                        {/* <Button variant="contained" size="small" onClick={onNextHandler}>Next</Button> */}
                                        <LoadingButton variant="contained" size="small" type="submit" loading={isCreatingOrder} sx={primaryBtnSx}>
                                            Create Shipment
                                        </LoadingButton>
                                        <Button variant="outlined" size="small" sx={secondaryBtnSx} onClick={() => reset()}>Reset</Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </FormProvider>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}

export default CreatePickupRequest2;
